import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import Shepherd from "shepherd.js";
import Swal from "sweetalert2";
import { isTextEntry } from "./softKeyboard";

// When Back closed each overlay. One stays in the DOM looking open through its exit
// animation (~200ms), and a quick second Back is meant for what is under it. The mark
// expires because a modal kept mounted can be opened again.
const closedAt = new WeakMap();
const CLOSING_MS = 500;
const isClosing = (el) => Date.now() - (closedAt.get(el) ?? -Infinity) < CLOSING_MS;
const markClosing = (el) => closedAt.set(el, Date.now());

// The WebView's Copy/Share toolbar doesn't take Back itself: clear the selection under it.
// (A selection inside a text field reads as collapsed; blurring the field clears that one.)
const clearSelection = () => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return false;
  selection.removeAllRanges();
  return true;
};

// Closes the topmost overlay, if any. Returns whether it did.
const closeTopOverlay = () => {
  // SweetAlert2 dialogs. Toasts aren't modal, so Back goes past them.
  const popup = Swal.isVisible() ? Swal.getPopup() : null;
  if (popup && !popup.classList.contains("swal2-toast") && !isClosing(popup)) {
    markClosing(popup);
    Swal.close();
    return true;
  }
  // Guided tours (including the welcome dialog).
  if (Shepherd.activeTour) {
    Shepherd.activeTour.cancel();
    return true;
  }
  // MUI dialogs, drawers, bottom sheets, menus and popovers: an Escape on the topmost
  // open one makes it call its own onClose, exactly like the Escape key on desktop.
  const open = [...document.querySelectorAll(".MuiModal-root")].filter(
    (el) =>
      !el.classList.contains("MuiModal-hidden") &&
      getComputedStyle(el).visibility !== "hidden" &&
      !isClosing(el)
  );
  const top = open[open.length - 1];
  if (top) {
    markClosing(top);
    top.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }));
    return true;
  }
  return false;
};

// A text field still focused after Back put its keyboard away (e.g. Home's search, its
// suggestions open): blurring it closes what it opened, as tapping elsewhere would. One in
// an overlay on its way out is going with it.
const blurTextField = () => {
  const el = document.activeElement;
  if (!isTextEntry(el) || isClosing(el.closest(".swal2-popup, .MuiModal-root"))) return false;
  el.blur();
  return true;
};

const isHome = () => ["", "#", "#/"].includes(window.location.hash);

// Android's system Back key / back gesture. Without a handler it finished the activity
// from every screen, losing the page, open dialogs and typed text. Now it clears a text
// selection, closes the topmost overlay or leaves a focused field, else goes back through
// the app's history, and from Home leaves the app the way Android 12+ leaves a root
// activity: minimized, so its state survives. Returns a cleanup function.
export const registerAndroidBackButton = () => {
  if (Capacitor.getPlatform() !== "android") return () => {};

  const listener = App.addListener("backButton", ({ canGoBack }) => {
    if (clearSelection() || closeTopOverlay() || blurTextField()) return;
    if (!isHome()) {
      if (canGoBack) window.history.back();
      else window.location.replace("#/"); // opened straight onto a deeper page
      return;
    }
    App.minimizeApp();
  });

  return () => {
    listener.then((handle) => handle.remove());
  };
};
